import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useApolloClient, useQuery } from '@apollo/client/react';
import {
  Button,
  Card,
  Dropdown,
  Flex,
  Popover,
  Skeleton,
  Space,
  Tabs,
  Tooltip,
} from 'antd';
import { DownOutlined, MenuOutlined, PhoneOutlined } from '@ant-design/icons';

import styled from '@emotion/styled';

import AccountStatusSection from './AccountStatusSection/AccountStatusSection';
import ActivateCardButton from './ActivateCardButton';
import CreditAccountSnapshot from './CreditAccountSnapshot';
import CreditAccountTitle from './CreditAccountTitle';
import CreditBalanceRefundSection from './CreditBalanceRefundSection/CreditBalanceRefundSection';
import CreditDetailsSection from './CreditDetailsSection/CreditDetailsSection';
import CvvVerifyModal from './CvvVerifyModal';
import FraudStatusBanner from 'components/FraudStatusBanner/FraudStatusBanner';
import FullStoryButton from './FullStoryButton';
import PaymentSection from './PaymentSection/PaymentSection';
import PaymentReturnSection from './PaymentReturnSection';
import OffersSection from './OffersSection/OffersSection';
import SendCardSection from './SendCardSection/SendCardSection';
import StatementsSection from './StatementsSection/StatementsSection';
import TransactionSection from './TransactionSection/TransactionSection';
import TsysIdentifiers from './TsysIdentifiers';
import ArchivedCreditAccountPage from './ArchivedCreditAccountPage';

import ApplyStatusModal from './ApplyStatusModal/ApplyStatusModal';
import AutopayModal from './AutopayModal/AutopayModal';
import MakePaymentModal from './MakePaymentModal';
import StatementPreferencesModal from './StatementPreferencesUpdateModal';
import BankruptcyBoundaryModal, {
  useBankruptcyBoundaryModal,
} from './BankruptcyBoundaryModal';

import CreditAccountStatus from 'components/CreditAccountStatus/CreditAccountStatus';
import DisplayValue from 'components/DisplayValue/DisplayValue';
import useAccountsInfo from 'hooks/useAccountsInfo';
import useAgent from 'hooks/useAgent';
import useModalControls from 'hooks/useModalControls';
import { CREDIT_ACCOUNT_VIEWS } from 'utilities/constants';

import { GET_ACCOUNT_STATEMENT_PREFERENCES } from 'queries/account';
import { GET_CURRENT_PRECO_OFFER } from 'queries/prechargeoff';
import { hasPrecoOffer } from 'utilities/helpers';
import { creditAccountPath } from 'utilities/routes';
import BankruptcyManagementModal from 'components/BankruptcyManagementModal/BankruptcyManagementModal';

// Immediate payments take a couple seconds to reflect in the PreCO offer, so we
// wait before each check instead of polling immediately.
const PRECO_OFFER_POLL_INTERVAL_MS = 1000;
const PRECO_OFFER_MAX_POLLS = 5;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getOfferVersion = (data) => {
  const offer = data?.prechargeoffSettlementOffer;
  return `${offer?.updatedAt}:${offer?.data?.remainingBalance}`;
};

const TitleSkeleton = styled(Skeleton)`
  height: var(--heading-height-4);
  vertical-align: center;
  .ant-skeleton-section {
    vertical-align: middle;
    .ant-skeleton-title {
      margin: 0;
      display: block;
    }
  }
`;

const ACCOUNT_ACTIONS = {
  APPLY_STATUS: 'apply-status',
  MANAGE_STATEMENT_PREFERENCES: 'manage-statement-preferences',
  MANAGE_BANKRUPTCY_STATUS: 'manage-bankruptcy-status',
};

const tabItems = [
  {
    label: 'Card Details',
    key: CREDIT_ACCOUNT_VIEWS.CARD_DETAILS,
    children: <CreditDetailsSection />,
  },
  {
    label: 'Account Statuses',
    key: CREDIT_ACCOUNT_VIEWS.ACCOUNT_STATUSES,
    children: <AccountStatusSection />,
  },
  {
    label: 'Transactions',
    key: CREDIT_ACCOUNT_VIEWS.TRANSACTIONS,
    children: <TransactionSection />,
  },
  {
    label: 'Payments',
    key: CREDIT_ACCOUNT_VIEWS.PAYMENTS,
    children: <PaymentSection />,
  },
  {
    label: 'Returned Payments',
    key: CREDIT_ACCOUNT_VIEWS.RETURNED_PAYMENTS,
    children: <PaymentReturnSection />,
  },
  {
    label: 'Statements',
    key: CREDIT_ACCOUNT_VIEWS.STATEMENTS,
    children: <StatementsSection />,
  },
  {
    label: 'Send Card',
    key: CREDIT_ACCOUNT_VIEWS.SEND_CARD,
    children: <SendCardSection />,
  },
  {
    label: 'Offers',
    key: CREDIT_ACCOUNT_VIEWS.OFFERS,
    children: <OffersSection />,
  },
  {
    label: 'CBR',
    key: CREDIT_ACCOUNT_VIEWS.CREDIT_BALANCE_REFUND,
    children: <CreditBalanceRefundSection />,
  },
];

const StatusAndTsysGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  width: 100%;
`;

export const AccountLevelActionsContext = createContext(null);

export const useAccountLevelActions = () => {
  const accountActions = useContext(AccountLevelActionsContext);
  return accountActions || {};
};

const CreditAccountPage = () => {
  const { accountId, customerId, view } = useParams();
  const navigate = useNavigate();
  const agent = useAgent();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const pathAction = queryParams.get('action');

  const applyStatusModal = useModalControls();
  const autopayModal = useModalControls();
  const bankruptcyModal = useModalControls();
  const cvvVerifyModal = useModalControls();
  const makePaymentModal = useModalControls();
  const statementPreferencesModal = useModalControls();

  const apolloClient = useApolloClient();
  const [madeImmediatePayment, setMadeImmediatePayment] = useState(false);

  const { accountsQuery, account, simpleAccount, accountInDebtSale } =
    useAccountsInfo();

  useEffect(() => {
    setMadeImmediatePayment(false);
  }, [accountId]);

  const precoOfferQuery = {
    query: GET_CURRENT_PRECO_OFFER,
    variables: { accountId },
  };

  // Until payment plans launch (phase 2), the agent workflow for enrolling an
  // offer is to schedule 3 payments, where the 1st can be an immediate payment
  // and the rest are scheduled. We refetch the PreCO data after that immediate
  // payment so the offer/eligibility reflects it right away. This is likely a
  // temporary solution that can be removed once payment plans go live.
  const refreshPrecoOffer = async () => {
    const cached = apolloClient.readQuery(precoOfferQuery);

    setMadeImmediatePayment(true);
    try {
      // No active offer: a payment can change eligibility and offer amounts.
      // Wait for the backend to catch up, then refetch eligibility (cascades to
      // the offers query when newly eligible) and the offers query (refreshes
      // amounts when already eligible).
      if (!hasPrecoOffer(cached?.prechargeoffSettlementOffer)) {
        await wait(PRECO_OFFER_POLL_INTERVAL_MS);
        await apolloClient
          .refetchQueries({
            include: ['GetPrecoEligibility', 'GetAvailablePrecoOffers'],
          })
          .catch(() => {
            // A transient failure shouldn't abort the remaining polls.
          });
        return;
      }

      // Active offer: poll until the offer reflects the payment, then stop early.
      // Using apolloClient.query (not the mounted query's refetch) avoids
      // flipping its loading state, so a single steady loading state shows until
      // the updated balances arrive.
      const versionBeforePayment = getOfferVersion(cached);
      for (let poll = 0; poll < PRECO_OFFER_MAX_POLLS; poll++) {
        await wait(PRECO_OFFER_POLL_INTERVAL_MS);
        try {
          const { data } = await apolloClient.query({
            ...precoOfferQuery,
            fetchPolicy: 'network-only',
          });
          if (getOfferVersion(data) !== versionBeforePayment) break;
        } catch {
          // A transient failure shouldn't abort the remaining polls.
        }
      }
    } finally {
      setMadeImmediatePayment(false);
    }
  };

  const {
    bankruptcyBoundaryModal: makePaymentBoundaryModal,
    checkBankruptcyBoundary: checkMakePaymentBoundary,
    onBankruptcyAcknowledged: onMakePaymentBankruptcyAcknowledged,
  } = useBankruptcyBoundaryModal(makePaymentModal.show);

  const handleAccountActionClick = (event) => {
    switch (event?.key) {
      case ACCOUNT_ACTIONS.APPLY_STATUS:
        applyStatusModal.show();
        break;
      case ACCOUNT_ACTIONS.MANAGE_STATEMENT_PREFERENCES:
        statementPreferencesModal.show();
        break;
      case ACCOUNT_ACTIONS.MANAGE_BANKRUPTCY_STATUS:
        bankruptcyModal.show();
        break;
    }
  };

  //REVIEW: error handling
  const statementPreferencesQuery = useQuery(
    GET_ACCOUNT_STATEMENT_PREFERENCES,
    {
      variables: { customerId, accountId },
      notifyOnNetworkStatusChange: true,
    },
  );

  const statementPreferences = useMemo(() => {
    const preferences =
      statementPreferencesQuery.data?.account?.statementPreferences || {};
    return {
      canUpdate: preferences.canUpdateStatementPreference || false,
      isPaperless: preferences.isPaperlessStatement || false,
    };
  }, [statementPreferencesQuery.data]);

  const actionItems = [
    {
      label: 'Apply Account Status',
      key: ACCOUNT_ACTIONS.APPLY_STATUS,
    },
    {
      label: `Statements: ${statementPreferences?.isPaperless ? 'Unenroll from Paperless' : 'Enroll in Paperless'}`,
      key: ACCOUNT_ACTIONS.MANAGE_STATEMENT_PREFERENCES,
    },
  ];

  if (agent.isSuperUser) {
    actionItems.push({
      label: 'Manage Bankruptcy Status',
      key: ACCOUNT_ACTIONS.MANAGE_BANKRUPTCY_STATUS,
    });
  }

  const renderTitle = () => {
    return (
      <Flex justify="space-between" gap="var(--spacing)">
        {accountsQuery.loading ? (
          <TitleSkeleton
            loading={true}
            active={true}
            paragraph={false}
          ></TitleSkeleton>
        ) : (
          <CreditAccountTitle
            extraContent={
              <Flex
                gap="var(--spacing-xs)"
                align="center"
                style={{
                  fontSize: 'var(--font-size)',
                  fontWeight: 'var(--font-weight)',
                }}
              >
                <Button size="small" onClick={cvvVerifyModal.show}>
                  Verify CVV
                </Button>

                <ActivateCardButton />

                <Space.Compact>
                  <Tooltip
                    title={
                      accountInDebtSale
                        ? 'Unable to make payments on this account because it’s part of a debt sale'
                        : ''
                    }
                  >
                    <Button
                      type="primary"
                      size="small"
                      disabled={accountInDebtSale}
                      onClick={checkMakePaymentBoundary}
                    >
                      Make Payment
                    </Button>
                  </Tooltip>
                  <Dropdown
                    menu={{
                      items: actionItems,
                      onClick: handleAccountActionClick,
                    }}
                    trigger={['click']}
                  >
                    <Button size="small">
                      <MenuOutlined />
                    </Button>
                  </Dropdown>
                </Space.Compact>
              </Flex>
            }
          />
        )}
      </Flex>
    );
  };

  useEffect(() => {
    if (pathAction) {
      //Need to make sure this only calls once we know if the customer is bankrupt
      if (pathAction === 'make-payment') {
        checkMakePaymentBoundary();
      }
    }
  }, [pathAction]);

  return (
    <AccountLevelActionsContext.Provider
      value={{
        applyStatusModal,
        autopayModal,
        cvvVerifyModal,
        madeImmediatePayment,
        statementPreferences,
        statementPreferencesModal,
      }}
    >
      <>
        <ApplyStatusModal {...applyStatusModal} />
        <AutopayModal {...autopayModal} />
        <BankruptcyManagementModal {...bankruptcyModal} />
        <BankruptcyBoundaryModal
          onBankruptcyAcknowledged={onMakePaymentBankruptcyAcknowledged}
          {...makePaymentBoundaryModal}
        />
        <CvvVerifyModal {...cvvVerifyModal} />
        <MakePaymentModal
          {...makePaymentModal}
          onImmediatePayment={refreshPrecoOffer}
        />
        <StatementPreferencesModal
          isPaperless={statementPreferences?.isPaperless}
          {...statementPreferencesModal}
        />
        <Card
          style={{ overflowY: 'hidden' }}
          styles={{
            body: { padding: 'var(--spacing)' },
            header: {
              padding: '0 var(--spacing)',
            },
          }}
          title={renderTitle()}
        >
          <Flex vertical={true} gap="var(--spacing-sm)">
            <FraudStatusBanner account={account} />
            <StatusAndTsysGrid>
              <CreditAccountStatus
                account={account}
                simpleAccount={simpleAccount}
              ></CreditAccountStatus>
              <Flex gap="var(--spacing-xs)">
                <TsysIdentifiers simpleAccount={simpleAccount} />
                <Popover
                  trigger="click"
                  arrow={false}
                  placement="bottomRight"
                  content={
                    <Flex vertical={true} gap="var(--spacing)">
                      <div>
                        <DisplayValue label="Collections" canCopy={true}>
                          (619) 937 3483
                        </DisplayValue>
                      </div>
                      <div>
                        <DisplayValue label="Disputes" canCopy={true}>
                          (866)-335-2390
                        </DisplayValue>
                      </div>
                      <div>
                        <DisplayValue label="Servicing" canCopy={true}>
                          (855) 790 8860
                        </DisplayValue>
                      </div>
                      <div>
                        <DisplayValue label="Recoveries" canCopy={true}>
                          (888) 695 8536
                        </DisplayValue>
                      </div>
                    </Flex>
                  }
                >
                  <Button size="small">
                    <PhoneOutlined />
                    <DownOutlined style={{ fontSize: 'var(--font-size-sm)' }} />
                  </Button>
                </Popover>
              </Flex>
            </StatusAndTsysGrid>
            <CreditAccountSnapshot />
            <Tabs
              activeKey={view || CREDIT_ACCOUNT_VIEWS.CARD_DETAILS}
              defaultActiveKey={CREDIT_ACCOUNT_VIEWS.CARD_DETAILS}
              onTabClick={(viewKey) => {
                navigate(creditAccountPath(customerId, accountId, viewKey), {
                  replace: true,
                });
              }}
              tabBarExtraContent={<FullStoryButton />}
              items={tabItems}
            />
          </Flex>
        </Card>
      </>
    </AccountLevelActionsContext.Provider>
  );
};

const ArchivalBoundary = () => {
  const { accountIsArchived } = useAccountsInfo();

  if (accountIsArchived) {
    return <ArchivedCreditAccountPage />;
  } else {
    return <CreditAccountPage />;
  }
};

export default ArchivalBoundary;
